import React, { useEffect, useState } from "react";
import { FaCalendarAlt, FaUserGraduate, FaCoins, FaVideo, FaCopy } from "react-icons/fa";
import { sharePrice, formatPieces, splitPayout } from "../../utils/pricing";
import {
  formatCourseDate,
  sessionStatus,
  hasCourseEnded,
  jitsiUrl,
  OPEN_BEFORE_MIN,
} from "../../utils/courseTime";
import { useLang } from "../../i18n/LanguageContext";

const CourseCard = ({
  course,
  professeurs = [],
  session = null, // { videoType, jitsiRoom, meetLink } — chargé par la page
  user = null,
  isOwner = false,
  deleteConfirm = false,
  toggleDeleteConfirm,
  deleteCourse,
  canDelete = false,
  onClaim, // (course) => Promise : paiement du prof après le cours
}) => {
  const prof = professeurs.find((p) => p.id === course.ownerId);
  const { t, lang } = useLang();

  // Rafraîchit l'état (salle ouverte / fermée) chaque minute
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(id);
  }, []);
  const [copied, setCopied] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimMsg, setClaimMsg] = useState(null);

  const ended = hasCourseEnded(course, now);
  const students = Number(course.students) || 0;
  const collectedEstimate = students ? sharePrice(course.price, students) * students : 0;
  const estimate = splitPayout(collectedEstimate);

  const handleClaim = async () => {
    setClaiming(true);
    setClaimMsg(null);
    try {
      const r = await onClaim(course);
      setClaimMsg({ ok: true, text: t("{n} ajoutées à votre portefeuille !", { n: formatPieces(r.prof, lang) }) });
    } catch (e) {
      setClaimMsg({ ok: false, text: t(e.message) });
    } finally {
      setClaiming(false);
    }
  };

  const status = sessionStatus(course, now);
  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.displayName || "";

  let joinUrl = "";
  let platform = "";
  if (session?.videoType === "custom" && session.meetLink) {
    joinUrl = session.meetLink;
    platform = /zoom\./i.test(joinUrl) ? "Zoom" : /meet\.google/i.test(joinUrl) ? "Google Meet" : t("lien du prof");
  } else if (session?.jitsiRoom) {
    joinUrl = jitsiUrl(session.jitsiRoom, displayName, course.name);
    platform = "Jitsi";
  }

  // Le prof peut ouvrir sa salle à tout moment (pour tester), les élèves au bon moment
  const canJoin = joinUrl && (isOwner ? status !== "ended" : status === "open");

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* presse-papier indisponible */
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-carte overflow-hidden flex flex-col">
      <div className="bg-prune px-5 py-4">
        <h3 className="text-white font-bold text-lg">{course.name}</h3>
        <p className="text-white/80 text-sm">
          {prof ? `${prof.firstName} ${prof.lastName}` : t("Professeur")}
          {course.subject ? ` · ${course.subject}` : ""}
        </p>
      </div>
      <div className="p-5 flex flex-col gap-3 flex-1 text-sm text-stone-600">
        <p className="line-clamp-2">{course.description}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-stone-500 mt-auto">
          <span className="flex items-center gap-1"><FaCalendarAlt className="text-prune" /> {formatCourseDate(course, lang)}</span>
          <span className="flex items-center gap-1"><FaUserGraduate className="text-prune" /> {course.students || 0}/{course.maxStudents}</span>
          <span className="flex items-center gap-1"><FaCoins className="text-or" />
            {isOwner
              ? t("Prix {n}", { n: formatPieces(course.price, lang) })
              : t("Ta part : {n}", { n: formatPieces(sharePrice(course.price, course.students || 1), lang) })}
          </span>
        </div>

        {/* ─── Salle vidéo ─── */}
        {!joinUrl ? (
          <p className="text-xs text-stone-400 italic">{t("Le lien de session sera communiqué par le professeur.")}</p>
        ) : status === "ended" ? (
          <p className="text-xs text-stone-400 italic text-center py-2">{t("Ce cours est terminé.")}</p>
        ) : canJoin ? (
          <a
            href={joinUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 py-2.5 rounded-full bg-prune text-white font-semibold hover:bg-prune-dark transition-colors"
          >
            <FaVideo /> {isOwner && status === "soon" ? t("Tester ma salle") : t("Rejoindre le cours")}
          </a>
        ) : (
          <div className="text-center py-2 rounded-full bg-stone-100 text-stone-500 text-xs font-semibold">
            🔒 {t("La salle ouvrira {n} min avant le début", { n: OPEN_BEFORE_MIN })}
          </div>
        )}

        {joinUrl && status !== "ended" && (
          <p className="text-[11px] text-stone-400 text-center -mt-1">
            {t("Visio via {p}", { p: platform })}
            {isOwner && platform === "Jitsi" && ` · ${t("arrivez en premier : Jitsi vous demandera de vous connecter (Google) pour ouvrir la salle")}`}
          </p>
        )}

        {isOwner && joinUrl && status !== "ended" && (
          <button onClick={copyLink} className="flex items-center justify-center gap-2 text-xs text-prune hover:underline">
            <FaCopy /> {copied ? t("Lien copié !") : t("Copier le lien de la salle")}
          </button>
        )}

        {/* ─── Paiement du prof ─── */}
        {isOwner && (
          <div className="bg-creme rounded-xl px-4 py-3 text-xs text-stone-600">
            {course.paidOut ? (
              <p className="text-green-700 font-semibold">
                ✓ {t("Payé : {n}", { n: formatPieces(course.payoutProf, lang) })}
                <span className="font-normal text-stone-500"> ({t("commission Tatie Soso : {n}", { n: formatPieces(course.payoutCommission, lang) })})</span>
              </p>
            ) : students === 0 ? (
              <p>{t("Aucun élève inscrit pour l'instant.")}</p>
            ) : ended ? (
              <button
                onClick={handleClaim}
                disabled={claiming || !onClaim}
                className="w-full py-2 rounded-full bg-or text-prune-deep font-semibold text-sm hover:bg-or-dark disabled:opacity-50"
              >
                {claiming ? t("Versement...") : `${t("Recevoir mes pièces")} — ${formatPieces(estimate.prof, lang)}`}
              </button>
            ) : (
              <p>
                {t("Après le cours, vous recevrez")} <strong className="text-prune">{formatPieces(estimate.prof, lang)}</strong>
                {" "}({t("{n} payées par les élèves − 10 % de commission", { n: formatPieces(estimate.total, lang) })}).
              </p>
            )}
            {claimMsg && (
              <p className={`mt-2 ${claimMsg.ok ? "text-green-700" : "text-red-600"}`}>{claimMsg.text}</p>
            )}
          </div>
        )}

        {canDelete && !ended && !course.paidOut && (
          <button
            onClick={() => (deleteConfirm ? deleteCourse(course.id) : toggleDeleteConfirm(course.id))}
            onMouseLeave={() => deleteConfirm && toggleDeleteConfirm(course.id)}
            className={`py-2 rounded-full font-semibold text-white transition-colors ${
              deleteConfirm ? "bg-red-600 hover:bg-red-700" : "bg-stone-400 hover:bg-stone-500"
            }`}
          >
            {deleteConfirm
              ? t("Confirmer ({n} élève(s) remboursé(s))", { n: course.students || 0 })
              : t("Supprimer le cours")}
          </button>
        )}
      </div>
    </div>
  );
};

export default CourseCard;
